import React, { useEffect, useState, useRef } from "react";
import { Grid, Typography, Button, Box } from "@mui/material";
import { Link } from "react-router-dom";
import theme from "../components/Theme";

const landingImages = [
  "/images/landing-grid/Gemini_Generated_Image_1t13gf1t13gf1t13.png",
  "/images/landing-grid/Gemini_Generated_Image_2zg1gt2zg1gt2zg1.png",
  "/images/landing-grid/Gemini_Generated_Image_81f5lb81f5lb81f5.png",
  "/images/landing-grid/Gemini_Generated_Image_8ul6uq8ul6uq8ul6.png",
  "/images/landing-grid/Gemini_Generated_Image_9aqwg89aqwg89aqw.png",
  "/images/landing-grid/Gemini_Generated_Image_9blgck9blgck9blg.png",
  "/images/landing-grid/Gemini_Generated_Image_aevnidaevnidaevn.png",
  "/images/landing-grid/Gemini_Generated_Image_axlt2yaxlt2yaxlt.png",
  "/images/landing-grid/Gemini_Generated_Image_cjuf9jcjuf9jcjuf.png",
  "/images/landing-grid/Gemini_Generated_Image_g9pcx5g9pcx5g9pc.png",
  "/images/landing-grid/Gemini_Generated_Image_k6cf8pk6cf8pk6cf.png",
  "/images/landing-grid/Gemini_Generated_Image_m2u5gym2u5gym2u5.png",
  "/images/landing-grid/Gemini_Generated_Image_qusfe3qusfe3qusf.png",
  "/images/landing-grid/Gemini_Generated_Image_rmvy8ermvy8ermvy.png",
  "/images/landing-grid/Gemini_Generated_Image_s482qts482qts482.png",
  "/images/landing-grid/Gemini_Generated_Image_sfz038sfz038sfz0.png",
  "/images/landing-grid/Gemini_Generated_Image_sr2d20sr2d20sr2d.png",
  "/images/landing-grid/Gemini_Generated_Image_uok1wauok1wauok1.png",
  "/images/landing-grid/Gemini_Generated_Image_wp5cfqwp5cfqwp5c.png",
  "/images/landing-grid/Gemini_Generated_Image_ww4rrwww4rrwww4r.png",
];

function Home() {
  const [leftGridImages, setLeftGridImages] = useState([]);
  const [rightGridImages, setRightGridImages] = useState([]);
  const [topGridCurrent, setTopGridCurrent] = useState([0, 1, 2]);
  const [bottomGridCurrent, setBottomGridCurrent] = useState([0, 1, 2]);
  const topGridIntervalRef = useRef(null);
  const bottomGridIntervalRef = useRef(null);

  // Split images into two grids
  useEffect(() => {
    const half = Math.ceil(landingImages.length / 2);
    setLeftGridImages(landingImages.slice(0, half));
    setRightGridImages(landingImages.slice(half));
  }, []);

  // Top grid carousel - random rotation every 5 seconds
  useEffect(() => {
    if (leftGridImages.length === 0) return;

    // Start after 1 second (staggered timing)
    const initialTimeout = setTimeout(() => {
      const rotateTopGrid = () => {
        setTopGridCurrent((prev) => {
          // Pick 3 random indices from leftGridImages
          const indices = new Set();
          while (indices.size < 3) {
            indices.add(Math.floor(Math.random() * leftGridImages.length));
          }
          return Array.from(indices);
        });
      };

      rotateTopGrid();
      topGridIntervalRef.current = setInterval(rotateTopGrid, 5000);
    }, 1000);

    return () => {
      clearTimeout(initialTimeout);
      if (topGridIntervalRef.current) {
        clearInterval(topGridIntervalRef.current);
      }
    };
  }, [leftGridImages]);

  // Bottom grid carousel - random rotation every 5 seconds (different start time)
  useEffect(() => {
    if (rightGridImages.length === 0) return;

    // Start after 3 seconds (staggered timing - different from top)
    const initialTimeout = setTimeout(() => {
      const rotateBottomGrid = () => {
        setBottomGridCurrent((prev) => {
          // Pick 3 random indices from rightGridImages
          const indices = new Set();
          while (indices.size < 3) {
            indices.add(Math.floor(Math.random() * rightGridImages.length));
          }
          return Array.from(indices);
        });
      };

      rotateBottomGrid();
      bottomGridIntervalRef.current = setInterval(rotateBottomGrid, 5000);
    }, 3000);

    return () => {
      clearTimeout(initialTimeout);
      if (bottomGridIntervalRef.current) {
        clearInterval(bottomGridIntervalRef.current);
      }
    };
  }, [rightGridImages]);

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: scale(1.05);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
      <Grid container spacing={0} sx={{ flex: 1, height: "100%", overflow: "hidden", margin: 0 }}>
        {/* Left side: Slogan and CTA */}
        <Grid item xs={12} sm={6} sx={{ height: "100%", overflow: "hidden" }}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              px: 4,
              py: 4,
            }}
          >
            <Typography variant="h1" sx={{ mb: 2 }}>
              Energize your life
            </Typography>
            <Typography variant="h4" sx={{ mb: 4, opacity: 0.8 }}>
              Anywhere. Anytime. Everyone
            </Typography>
            <Button
              component={Link}
              to="/places"
              variant="contained"
              sx={{
                p: 3,
                fontSize: "24px",
                minWidth: "200px",
                borderRadius: "50px",
                backgroundColor: theme.palette.accent.main,
                "&:hover": {
                  backgroundColor: theme.palette.accent.dark,
                },
              }}
            >
              LET'S GO
            </Button>
          </Box>
        </Grid>

        {/* Right side: Two rotating image grids */}
        <Grid item xs={12} sm={6} sx={{ height: "100%", overflow: "hidden" }}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              pr: 2, // Padding on right to prevent images touching edge
            }}
          >
            {/* Top grid - random rotation */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                overflow: "hidden",
                borderBottom: "2px solid #fff",
                gap: "2px",
              }}
            >
              {topGridCurrent.map((imageIndex, index) => (
                <Box
                  key={`top-${imageIndex}-${index}`}
                  sx={{
                    flex: 1,
                    overflow: "hidden",
                    borderRadius: 2, // Rounded edges
                    position: "relative",
                  }}
                >
                  <img
                    src={leftGridImages[imageIndex]}
                    alt={`Landing image ${imageIndex + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      borderRadius: "8px", // Match Box borderRadius
                      transition: "opacity 0.8s ease-in-out, transform 0.8s ease-in-out",
                      animation: "fadeIn 0.8s ease-in-out",
                    }}
                  />
                </Box>
              ))}
            </Box>

            {/* Bottom grid - random rotation */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                overflow: "hidden",
                gap: "2px",
              }}
            >
              {bottomGridCurrent.map((imageIndex, index) => (
                <Box
                  key={`bottom-${imageIndex}-${index}`}
                  sx={{
                    flex: 1,
                    overflow: "hidden",
                    borderRadius: 2, // Rounded edges
                    position: "relative",
                  }}
                >
                  <img
                    src={rightGridImages[imageIndex]}
                    alt={`Landing image ${imageIndex + 10}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      borderRadius: "8px", // Match Box borderRadius
                      transition: "opacity 0.8s ease-in-out, transform 0.8s ease-in-out",
                      animation: "fadeIn 0.8s ease-in-out",
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
    </>
  );
}

export default Home;
